import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextInput from '@/components/common/TextInput';
import { useTranslation } from '@/hooks/useTranslation';
import { NAMESPACES } from '@/i18n/namespaces';
import { useRegister, useVerifyAge, useVerifyEmail } from '@/features/auth/hooks/useAuth';
import { RegisterCredentials } from '@/features/auth/types';
import { toast } from 'react-toastify';

const Signup: React.FC = () => {
  const [birthday, setBirthday] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'birthday' | 'email' | 'name' | 'password'>('birthday');
  const [direction, setDirection] = useState<'forward' | 'backward' | 'none'>('none');
  const [ageVerified, setAgeVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation(NAMESPACES.AUTH);
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  const { register, loading: registering } = useRegister();
  const { checkAge, loading: verifyingAge } = useVerifyAge();
  const { checkEmail, loading: verifyingEmail } = useVerifyEmail();
  
  const isLoading = registering || verifying || verifyingAge || verifyingEmail;

  // Get animation class
  const getAnimationClass = (currentStep: 'birthday' | 'email' | 'name' | 'password') => {
    if (direction === 'none') {
      return 'animate-fade-in';
    }
    
    if (step === currentStep) {
      return 'animate-fade-in';
    } else {
      return 'animate-fade-out';
    }
  };
  
  const handleBirthdayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBirthday(e.target.value);
    // Reset age verification when birthday changes
    setAgeVerified(false);
  };
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Reset email verification when email changes
    setEmailVerified(false);
  };
  
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };
  
  const verifyUserAge = async () => {
    if (!birthday) return false;
    
    setVerifying(true);
    const result = await checkAge(birthday);
    setVerifying(false);
    
    if (result) {
      setAgeVerified(true);
      return true;
    } else {
      // Remove duplicate toast - error already shown from API
      setAgeVerified(false);
      return false;
    }
  };
  
  const verifyUserEmail = async () => {
    if (!email) return false;
    
    setVerifying(true);
    const result = await checkEmail(email);
    setVerifying(false);
    
    if (result) {
      setEmailVerified(true);
      return true;
    } else {
      // Error already shown from API
      setEmailVerified(false);
      return false;
    }
  };
  
  const handleNextStep = async () => {
    if (step === 'birthday') {
      // Verify age when moving from birthday step
      const isAgeValid = await verifyUserAge();
      if (!isAgeValid) return;
    } else if (step === 'email') {
      // Verify email when moving from email step
      const isEmailValid = await verifyUserEmail();
      if (!isEmailValid) return;
    }
    
    setDirection('forward');
    // Add a small delay to allow animation to complete
    setTimeout(() => {
      if (step === 'birthday') {
        setStep('email');
      } else if (step === 'email') {
        setStep('name');
      } else if (step === 'name') {
        setStep('password');
      }
    }, 200);
  };
  
  const handleBack = () => {
    if (step === 'email') {
      setDirection('backward');
      setTimeout(() => {
        setStep('birthday');
      }, 200);
    } else if (step === 'name') {
      setDirection('backward');
      setTimeout(() => {
        setStep('email');
      }, 200);
    } else if (step === 'password') {
      setDirection('backward');
      setTimeout(() => {
        setStep('name');
      }, 200);
    } else {
      navigate('/');
    }
  };
  
  const handleCreateAccount = async () => {
    if (!email || !password || !birthday || !name || !ageVerified || !emailVerified) {
      toast.error(tErrors('auth.requiredFields'));
      return;
    }
    
    const credentials: RegisterCredentials = {
      name,
      email,
      password,
      confirmPassword: password, // We don't have confirm password field, so use the same value
      dateOfBirth: birthday, // Add date of birth to the registration credentials
    };
    
    await register(credentials);
  };
  
  // Handle key press events
  const handleBirthdayKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && birthday.trim()) {
      handleNextStep();
    }
  };
  
  const handleEmailKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && email.trim()) {
      handleNextStep();
    }
  };
  
  const handleNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && name.trim()) {
      handleNextStep();
    }
  };
  
  const handlePasswordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && password.trim() && !isLoading) {
      handleCreateAccount();
    }
  };
  
  return (
    <div className="h-dvh flex flex-col bg-black text-white">
      <div className="flex-1 flex flex-col px-6">
        {/* Birthday Step */}
        {step === 'birthday' && (
          <div className={`flex-1 flex flex-col ${getAnimationClass('birthday')}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('register.journey')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('register.birthday')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with inputs */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="date"
                  name="birthday"
                  placeholder={t('register.birthdayPlaceholder')}
                  value={birthday}
                  onChange={handleBirthdayChange}
                  onKeyPress={handleBirthdayKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('register.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleNextStep}
                disabled={!birthday.trim() || isLoading}
              >
                {verifying ? 'Verifying age...' : t('register.next')}
              </button>
            </div>
          </div>
        )}
        
        {/* Email Step */}
        {step === 'email' && (
          <div className={`flex-1 flex flex-col ${getAnimationClass('email')}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('register.emailQuestion')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('register.emailPrompt')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="email"
                  name="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={handleEmailChange}
                  onKeyPress={handleEmailKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('register.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleNextStep}
                disabled={!email.trim() || isLoading}
              >
                {verifying ? 'Verifying email...' : t('register.next')}
              </button>
            </div>
          </div>
        )}
        
        {/* Name Step */}
        {step === 'name' && (
          <div className={`flex-1 flex flex-col ${getAnimationClass('name')}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('register.nameQuestion')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('register.namePrompt')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="text"
                  name="name"
                  placeholder={t('register.namePlaceholder')}
                  value={name}
                  onChange={handleNameChange}
                  onKeyPress={handleNameKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('register.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleNextStep}
                disabled={!name.trim() || isLoading}
              >
                {t('register.next')}
              </button>
            </div>
          </div>
        )}
        
        {/* Password Step */}
        {step === 'password' && (
          <div className={`flex-1 flex flex-col ${getAnimationClass('password')}`}>
            {/* Top section with headings */}
            <div className="pt-[32px] mb-16 flex justify-center">
              <div className="max-w-[260px]">
                <h1 className="text-white text-center text-[27px] font-medium leading-6 mb-[12px]" 
                  style={{
                    letterSpacing: '-0.337px'
                  }}>
                  {t('register.setPassword')}
                </h1>
                <h2 className="text-center text-[18px] font-normal leading-5" 
                  style={{
                    color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
                    letterSpacing: '-0.225px'
                  }}>
                  {t('register.passwordPrompt')}
                </h2>
              </div>
            </div>
            
            {/* Middle section with input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <TextInput
                  type="password"
                  name="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyPress={handlePasswordKeyPress}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>
            
            {/* Bottom section with buttons */}
            <div className="flex justify-center items-center gap-4 mb-[32px]">
              <button 
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={handleBack}
                disabled={isLoading}
              >
                {t('register.back')}
              </button>
              
              <button 
                className="btn btn-primary whitespace-nowrap"
                onClick={handleCreateAccount}
                disabled={!password.trim() || isLoading}
              >
                {isLoading ? t('register.creatingAccount') : t('register.createAccount')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup; 